import { z } from 'zod';

import type { BookingPolicyDocument, BookingPolicyText } from '@mds/shared/types/content';

import { requiredText } from '@/lib/validation';

/**
 * The booking policy as the editor holds it. Sections carry a local key so a
 * reordered or removed one keeps its own inputs; the key never leaves the form.
 */

export type PolicyLanguage = 'en' | 'ar';

export interface SectionDraft {
  key: string;
  heading: string;
  body: string;
}

export interface PolicyTextDraft {
  title: string;
  sections: SectionDraft[];
}

export interface PolicyDraft {
  pdfFileId: string | null;
  en: PolicyTextDraft;
  ar: PolicyTextDraft;
}

/** The API's cap, so Add section stops where a save would be refused. */
export const MAX_SECTIONS = 60;

export const LANGUAGE_LABELS: Record<PolicyLanguage, string> = { en: 'English', ar: 'العربية' };

let nextKey = 0;
const key = () => `section-${(nextKey += 1)}`;

export const blankSection = (): SectionDraft => ({ key: key(), heading: '', body: '' });

const textDraftOf = (text: BookingPolicyText): PolicyTextDraft => ({
  title: text.title,
  sections: text.sections.map((s) => ({ key: key(), heading: s.heading, body: s.body })),
});

const textOf = (draft: PolicyTextDraft): BookingPolicyText => ({
  title: draft.title.trim(),
  sections: draft.sections.map((s) => ({ heading: s.heading.trim(), body: s.body.trim() })),
});

export const draftOf = (doc: BookingPolicyDocument): PolicyDraft => ({
  pdfFileId: doc.pdfFileId,
  en: textDraftOf(doc.en),
  ar: textDraftOf(doc.ar),
});

export const documentOf = (draft: PolicyDraft): BookingPolicyDocument => ({
  pdfFileId: draft.pdfFileId,
  en: textOf(draft.en),
  ar: textOf(draft.ar),
});

export const isDirty = (draft: PolicyDraft, saved: BookingPolicyDocument) =>
  JSON.stringify(documentOf(draft)) !== JSON.stringify(saved);

const textSchema = z.object({
  title: requiredText(160),
  sections: z
    .array(z.object({ heading: requiredText(200), body: requiredText(20_000) }))
    .max(MAX_SECTIONS, `Up to ${MAX_SECTIONS} sections.`),
});

/** Mirrors the API's `bookingPolicyDocumentSchema`. */
export const policySchema = z.object({ en: textSchema, ar: textSchema });
